import { createClient } from '@/lib/supabase/client';
import { queryCache, buildSearchKey, memoFilter, clearMemoPrefix } from '@/lib/supabase/queryCache';
import { subscriptionBatcher } from '@/lib/supabase/subscriptionBatcher';
import { recordAudit } from './auditLogService';

export interface CrewMember {
  id: string;
  crewCode: string;
  name: string;
  role: 'Driver' | 'Conductor';
  badgeOrLicense: string;
  depot: string;
  phone: string;
  grade: string;
  status: 'On Trip' | 'Rest' | 'Leave' | 'Available' | 'Suspended';
  joiningDate: string;
  route: string;
  certifications: string[];
  photoUrl?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

function mapRow(row: any): CrewMember {
  return {
    id: row.id,
    crewCode: row.crew_code,
    name: row.name,
    role: row.role,
    badgeOrLicense: row.badge_or_license,
    depot: row.depot,
    phone: row.phone ?? '',
    grade: row.grade,
    status: row.status,
    joiningDate: row.joining_date ?? '',
    route: row.route ?? '',
    certifications: row.certifications ?? [],
    photoUrl: row.photo_url ?? '',
    notes: row.notes ?? '',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

const CACHE_KEY = 'crew_members';

export interface CrewSearchParams {
  role?: 'Driver' | 'Conductor';
  status?: string;
  depot?: string;
  grade?: string;
  search?: string; // matches name, crew_code, badge_or_license
}

export const crewService = {
  async getAll(): Promise<CrewMember[]> {
    return queryCache.dedupe(CACHE_KEY, async () => {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('crew_members')
        .select('id,crew_code,name,role,badge_or_license,depot,phone,grade,status,joining_date,route,certifications,photo_url,notes,created_at,updated_at')
        .order('created_at', { ascending: true });
      if (error) { console.error('crewService.getAll:', error.message); return []; }
      let result = (data ?? []).map(mapRow);
      queryCache.set(CACHE_KEY, result);
      clearMemoPrefix('crew_search:');
      return result;
    });
  },

  /**
   * Server-side filtered query using indexed columns.
   * Results are cached per unique param combination.
   */
  async search(params: CrewSearchParams): Promise<CrewMember[]> {
    const cacheKey = buildSearchKey('crew_search', params as Record<string, unknown>);
    const cached = queryCache.get<CrewMember[]>(cacheKey);
    if (cached) return cached;

    const supabase = createClient();
    let query = supabase
      .from('crew_members')
      .select('*')
      .order('created_at', { ascending: true });

    if (params.role)   query = query.eq('role', params.role);
    if (params.status) query = query.eq('status', params.status);
    if (params.depot)  query = query.eq('depot', params.depot);
    if (params.grade)  query = query.eq('grade', params.grade);

    const { data, error } = await query;
    if (error) { console.error('crewService.search:', error.message); return []; }

    let result = (data ?? []).map(mapRow);

    // Client-side text search across name / crew_code / badge
    if (params.search) {
      const term = params.search.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(term) ||
          c.crewCode.toLowerCase().includes(term) ||
          c.badgeOrLicense.toLowerCase().includes(term)
      );
    }

    queryCache.set(cacheKey, result, 20_000);
    return result;
  },

  /**
   * Client-side memoized filter over the full cached list.
   * Use when the full list is already loaded and you need instant UI filtering.
   */
  filterMemo(allCrew: CrewMember[], params: CrewSearchParams): CrewMember[] {
    const memoKey = buildSearchKey('crew_memo', params as Record<string, unknown>);
    return memoFilter(memoKey, allCrew, (items) => {
      let result = items;
      if (params.role)   result = result.filter((c) => c.role === params.role);
      if (params.status) result = result.filter((c) => c.status === params.status);
      if (params.depot)  result = result.filter((c) => c.depot === params.depot);
      if (params.grade)  result = result.filter((c) => c.grade === params.grade);
      if (params.search) {
        const term = params.search!.toLowerCase();
        result = result.filter(
          (c) =>
            c.name.toLowerCase().includes(term) ||
            c.crewCode.toLowerCase().includes(term) ||
            c.badgeOrLicense.toLowerCase().includes(term)
        );
      }
      return result;
    });
  },

  async getNextCrewCode(role: 'Driver' | 'Conductor'): Promise<string> {
    const supabase = createClient();
    const prefix = role === 'Driver' ? 'DRI' : 'CON';
    const { data, error } = await supabase
      .from('crew_members')
      .select('crew_code')
      .eq('role', role)
      .order('crew_code', { ascending: false });
    if (error) { console.error('crewService.getNextCrewCode:', error.message); }
    let maxNum = 0;
    if (data && data.length > 0) {
      for (const row of data) {
        const parts = (row.crew_code as string).split('-');
        const num = parseInt(parts[1] ?? '0', 10);
        if (!isNaN(num) && num > maxNum) maxNum = num;
      }
    }
    const next = (maxNum + 1).toString().padStart(2, '0');
    return `${prefix}-${next}`;
  },

  async getById(id: string): Promise<CrewMember | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('crew_members')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) { console.error('crewService.getById:', error.message); return null; }
    return data ? mapRow(data) : null;
  },

  async create(member: Omit<CrewMember, 'id' | 'createdAt' | 'updatedAt'>): Promise<CrewMember | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('crew_members')
      .insert({
        crew_code: member.crewCode,
        name: member.name,
        role: member.role,
        badge_or_license: member.badgeOrLicense,
        depot: member.depot,
        phone: member.phone,
        grade: member.grade,
        status: member.status,
        joining_date: member.joiningDate || null,
        route: member.route,
        certifications: member.certifications,
        photo_url: member.photoUrl || null,
        notes: member.notes,
      })
      .select()
      .single();
    if (error) {
      console.error('crewService.create:', error.message, error.details, error.hint);
      throw new Error(error.message);
    }
    if (!data) {
      // Insert succeeded but SELECT returned nothing (e.g. RLS blocked read-back).
      // Re-fetch by crew_code so we can still return a valid object.
      const { data: refetched, error: refetchErr } = await supabase
        .from('crew_members')
        .select('*')
        .eq('crew_code', member.crewCode)
        .maybeSingle();
      if (refetchErr) throw new Error(refetchErr.message);
      if (!refetched) throw new Error('Crew member was inserted but could not be retrieved.');
      queryCache.invalidate(CACHE_KEY);
      queryCache.invalidatePrefix('crew_search:');
      clearMemoPrefix('crew_memo:');
      let result = mapRow(refetched);
      await recordAudit({
        action: 'crew_created',
        target_type: 'crew_member',
        target_id: result.id,
        target_name: result.name,
        change_details: { role: result.role, depot: result.depot, crew_code: result.crewCode },
      });
      return result;
    }
    queryCache.invalidate(CACHE_KEY);
    queryCache.invalidatePrefix('crew_search:');
    clearMemoPrefix('crew_memo:');
    let result = mapRow(data);
    await recordAudit({
      action: 'crew_created',
      target_type: 'crew_member',
      target_id: result.id,
      target_name: result.name,
      change_details: { role: result.role, depot: result.depot, crew_code: result.crewCode },
    });
    return result;
  },

  async update(id: string, updates: Partial<CrewMember>): Promise<CrewMember | null> {
    const supabase = createClient();
    const payload: any = {};
    if (updates.name !== undefined) payload.name = updates.name;
    if (updates.role !== undefined) payload.role = updates.role;
    if (updates.badgeOrLicense !== undefined) payload.badge_or_license = updates.badgeOrLicense;
    if (updates.depot !== undefined) payload.depot = updates.depot;
    if (updates.phone !== undefined) payload.phone = updates.phone;
    if (updates.grade !== undefined) payload.grade = updates.grade;
    if (updates.status !== undefined) payload.status = updates.status;
    if (updates.joiningDate !== undefined) payload.joining_date = updates.joiningDate || null;
    if (updates.route !== undefined) payload.route = updates.route;
    if (updates.certifications !== undefined) payload.certifications = updates.certifications;
    if (updates.notes !== undefined) payload.notes = updates.notes;

    const { data, error } = await supabase
      .from('crew_members')
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (error) { console.error('crewService.update:', error.message); return null; }
    queryCache.invalidate(CACHE_KEY);
    queryCache.invalidatePrefix('crew_search:');
    clearMemoPrefix('crew_memo:');
    let result = mapRow(data);
    const changedFields = Object.keys(payload).join(', ');
    await recordAudit({
      action: 'crew_updated',
      target_type: 'crew_member',
      target_id: result.id,
      target_name: result.name,
      change_details: { fields_changed: changedFields, ...payload },
    });
    return result;
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    // Fetch name before deleting for audit record
    const { data: existing } = await supabase.from('crew_members').select('name, crew_code').eq('id', id).maybeSingle();
    const { error } = await supabase.from('crew_members').delete().eq('id', id);
    if (error) { console.error('crewService.delete:', error.message); return false; }
    queryCache.invalidate(CACHE_KEY);
    queryCache.invalidatePrefix('crew_search:');
    clearMemoPrefix('crew_memo:');
    await recordAudit({
      action: 'crew_deleted',
      target_type: 'crew_member',
      target_id: id,
      target_name: existing?.name ?? id,
      change_details: { crew_code: existing?.crew_code ?? '' },
    });
    return true;
  },

  subscribeToChanges(callback: (crew: CrewMember[]) => void) {
    const supabase = createClient();
    const channel = supabase
      .channel('crew_members_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crew_members' }, () => {
        queryCache.invalidate(CACHE_KEY);
        queryCache.invalidatePrefix('crew_search:');
        clearMemoPrefix('crew_memo:');
        subscriptionBatcher.schedule(
          CACHE_KEY,
          () => crewService.getAll(),
          callback
        );
      })
      .subscribe();
    return () => {
      subscriptionBatcher.cancel(CACHE_KEY);
      supabase.removeChannel(channel);
    };
  },
};
